import { type ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button } from '../../components/ui/Button'
import { useToast } from '../../components/ui/Toast'
import { tutorialPorId, type PassoTutorial } from '../../tutoriais'
import { TutorialContext, type TutorialApi } from './tutorialContext'
import { useAtualizarOnboarding, useOnboarding } from './useOnboarding'

/** Espera pelo alvo antes de pular o passo; maior logo após um clique (modal abrindo, dados carregando). */
const ESPERA_MS = 1200
const ESPERA_APOS_CLIQUE_MS = 6000
const FOLGA = 6
const MARGEM = 12

interface Ativo {
  id: string
  /** Índices dos passos a percorrer (todos, ou só o grupo de um passo do "Primeiros passos"). */
  indices: number[]
  pos: number
  /** Percorre o tutorial inteiro (só então terminar marca como concluído). */
  completo: boolean
  /** Chegou por clique no alvo anterior: espera mais pelo próximo alvo. */
  paciente: boolean
}

interface Caixa { top: number; left: number; width: number; height: number }

function achar(alvo: string): HTMLElement | null {
  for (const el of document.querySelectorAll<HTMLElement>(`[data-tutorial="${alvo}"]`)) {
    const r = el.getBoundingClientRect()
    if (r.width > 0 && r.height > 0) return el
  }
  return null
}

const mesma = (a: Caixa | null, b: Caixa) =>
  !!a && a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height

export function TutorialProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const toast = useToast()
  const { data: onboarding } = useOnboarding()
  const atualizar = useAtualizarOnboarding()

  const [ativo, setAtivo] = useState<Ativo | null>(null)
  const ativoRef = useRef<Ativo | null>(null)
  const sentido = useRef<'f' | 'b'>('f')
  const mostrou = useRef(false)
  const pathRef = useRef(pathname)
  const onboardingRef = useRef(onboarding)
  useEffect(() => { pathRef.current = pathname; onboardingRef.current = onboarding })

  const mudar = useCallback((a: Ativo | null) => { ativoRef.current = a; setAtivo(a) }, [])

  const encerrar = useCallback((motivo: 'pular' | 'concluir' | 'abandono' | 'nada') => {
    const a = ativoRef.current
    if (!a) return
    mudar(null)
    if (!mostrou.current) {
      if (motivo === 'nada') toast.error('Não encontramos onde mostrar este guia nesta tela. Abra a tela do passo e tente de novo.')
      return
    }
    // Pular e abandonar marcam como visto; só terminar o guia inteiro marca como concluído.
    const concluido = motivo === 'concluir' && a.completo
    const anterior = onboardingRef.current?.estado.tutoriais[a.id]
    atualizar.mutate({ tutoriais: { [a.id]: { vistoEm: new Date().toISOString(), concluido: concluido || !!anterior?.concluido } } })
  }, [atualizar, mudar, toast])

  const ir = useCallback((pos: number, dir: 'f' | 'b', paciente = false) => {
    const a = ativoRef.current
    if (!a) return
    if (pos >= a.indices.length) { encerrar(mostrou.current ? 'concluir' : 'nada'); return }
    sentido.current = pos < 0 ? 'f' : dir
    mudar({ ...a, pos: Math.max(0, pos), paciente })
  }, [encerrar, mudar])

  const iniciarTutorial = useCallback((id: string, aPartirDoPasso?: string | number) => {
    const t = tutorialPorId(id)
    if (!t || t.passos.length === 0) return
    let indices = t.passos.map((_, i) => i)
    let pos = 0
    let completo = true
    if (typeof aPartirDoPasso === 'number') {
      pos = Math.min(Math.max(aPartirDoPasso, 0), indices.length - 1)
    } else if (aPartirDoPasso) {
      const grupo = indices.filter((i) => t.passos[i].passoOnboarding === aPartirDoPasso)
      if (grupo.length > 0) { indices = grupo; completo = false }
      else {
        const i = t.passos.findIndex((p) => p.id === aPartirDoPasso)
        if (i >= 0) pos = i
      }
    }
    sentido.current = 'f'
    mostrou.current = false
    mudar({ id, indices, pos, completo, paciente: false })
  }, [mudar])

  const encerrarTutorial = useCallback(() => encerrar('pular'), [encerrar])

  // Rota do passo (se houver): navega antes de procurar o alvo.
  useEffect(() => {
    if (!ativo) return
    const rota = tutorialPorId(ativo.id)?.passos[ativo.indices[ativo.pos]]?.rotas?.web
    if (rota && !pathRef.current.startsWith(rota.split('?')[0])) navigate(rota)
  }, [ativo, navigate])

  const api = useMemo<TutorialApi>(
    () => ({ iniciarTutorial, encerrarTutorial, ativoId: ativo?.id ?? null }),
    [iniciarTutorial, encerrarTutorial, ativo?.id],
  )

  const passo = ativo ? tutorialPorId(ativo.id)?.passos[ativo.indices[ativo.pos]] : undefined

  function avancar(porClique: boolean) {
    if (!ativo || !passo) return
    // Passar pelo passo de categorias conta como "categorias revisadas" (o painel marca o passo como feito).
    if (passo.passoOnboarding === 'categorias') atualizar.mutate({ categoriasRevisadas: true })
    ir(ativo.pos + 1, 'f', porClique)
  }

  return (
    <TutorialContext.Provider value={api}>
      {children}
      {ativo && passo && (
        <Guia
          key={`${ativo.id}:${ativo.pos}`}
          passo={passo}
          n={ativo.pos + 1}
          total={ativo.indices.length}
          paciente={ativo.paciente}
          onMostrou={() => { mostrou.current = true }}
          onVoltar={() => ir(ativo.pos - 1, 'b')}
          onProximo={() => avancar(false)}
          onClique={() => avancar(true)}
          onPular={() => encerrar('pular')}
          onSemAlvo={(perdeu) => {
            if (perdeu) { encerrar('abandono'); return }
            if (sentido.current === 'b' && ativo.pos > 0) ir(ativo.pos - 1, 'b')
            else ir(ativo.pos + 1, 'f')
          }}
        />
      )}
    </TutorialContext.Provider>
  )
}

interface GuiaProps {
  passo: PassoTutorial
  n: number
  total: number
  paciente: boolean
  onMostrou: () => void
  onVoltar: () => void
  onProximo: () => void
  onClique: () => void
  onPular: () => void
  /** `perdeu`: o alvo chegou a aparecer e sumiu (a pessoa saiu da tela). */
  onSemAlvo: (perdeu: boolean) => void
}

function Guia({ passo, n, total, paciente, onMostrou, onVoltar, onProximo, onClique, onPular, onSemAlvo }: GuiaProps) {
  const [caixa, setCaixa] = useState<Caixa | null>(null)
  const [altura, setAltura] = useState(0)
  const alvoRef = useRef<HTMLElement | null>(null)
  const achou = useRef(false)
  const falta = useRef<number | null>(null)
  const disparou = useRef(false)
  const focou = useRef(false)
  const balaoRef = useRef<HTMLDivElement>(null)
  const cb = useRef({ onMostrou, onClique, onPular, onSemAlvo })
  useEffect(() => { cb.current = { onMostrou, onClique, onPular, onSemAlvo } })

  useEffect(() => {
    const espera = paciente ? ESPERA_APOS_CLIQUE_MS : ESPERA_MS
    let raf = 0
    falta.current = Date.now()
    const ro = new ResizeObserver(() => agendar())

    function calcular() {
      let el = alvoRef.current
      if (!el || !el.isConnected || el.getBoundingClientRect().width === 0) el = achar(passo.alvo)
      if (!el) {
        alvoRef.current = null
        setCaixa(null)
        if (falta.current == null) falta.current = Date.now()
        return
      }
      falta.current = null
      if (el !== alvoRef.current) {
        if (alvoRef.current) ro.unobserve(alvoRef.current)
        alvoRef.current = el
        ro.observe(el)
        el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' })
        if (!achou.current) { achou.current = true; cb.current.onMostrou() }
      }
      const r = el.getBoundingClientRect()
      const c = { top: r.top, left: r.left, width: r.width, height: r.height }
      setCaixa((prev) => (mesma(prev, c) ? prev : c))
    }
    function agendar() {
      if (raf) return
      raf = requestAnimationFrame(() => { raf = 0; calcular() })
    }
    const intervalo = window.setInterval(() => {
      calcular()
      if (falta.current != null && !disparou.current && Date.now() - falta.current > espera) {
        disparou.current = true
        cb.current.onSemAlvo(achou.current)
      }
    }, 150)
    const mo = new MutationObserver(agendar)
    mo.observe(document.body, { childList: true, subtree: true, attributes: true })
    window.addEventListener('scroll', agendar, true)
    window.addEventListener('resize', agendar)

    // O clique real no alvo segue normalmente; só avançamos depois dele.
    const aoClicar = (e: MouseEvent) => {
      const el = alvoRef.current
      if (passo.avanco === 'clique' && el && e.target instanceof Node && el.contains(e.target) && !disparou.current) {
        disparou.current = true
        window.setTimeout(() => cb.current.onClique(), 0)
      }
    }
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      e.preventDefault()
      disparou.current = true
      cb.current.onPular()
    }
    document.addEventListener('click', aoClicar, true)
    window.addEventListener('keydown', aoTeclar, true)
    calcular()

    return () => {
      window.clearInterval(intervalo)
      if (raf) cancelAnimationFrame(raf)
      ro.disconnect()
      mo.disconnect()
      window.removeEventListener('scroll', agendar, true)
      window.removeEventListener('resize', agendar)
      document.removeEventListener('click', aoClicar, true)
      window.removeEventListener('keydown', aoTeclar, true)
    }
  }, [passo.alvo, passo.avanco, paciente])

  const visivel = caixa != null
  useLayoutEffect(() => {
    if (balaoRef.current) setAltura(balaoRef.current.offsetHeight)
  }, [visivel, n, passo.texto])
  useEffect(() => {
    if (visivel && !focou.current) { focou.current = true; balaoRef.current?.focus({ preventScroll: true }) }
  }, [visivel])

  if (!caixa) return null

  const vw = window.innerWidth
  const vh = window.innerHeight
  // Recorte com folga, limitado à janela (alvo maior que a tela fica recortado nas bordas).
  const top = Math.max(caixa.top - FOLGA, 0)
  const left = Math.max(caixa.left - FOLGA, 0)
  const bottom = Math.min(caixa.top + caixa.height + FOLGA, vh)
  const right = Math.min(caixa.left + caixa.width + FOLGA, vw)
  const furo = { top, left, width: Math.max(right - left, 0), height: Math.max(bottom - top, 0) }

  const largura = Math.min(380, vw - 2 * MARGEM)
  const h = altura || 190
  const abaixo = vh - bottom
  const acima = top
  let topoBalao: number
  if (abaixo >= h + MARGEM * 2) topoBalao = bottom + MARGEM
  else if (acima >= h + MARGEM * 2) topoBalao = top - MARGEM - h
  else if (abaixo >= acima) topoBalao = Math.min(bottom + MARGEM, vh - h - MARGEM)
  else topoBalao = Math.max(top - MARGEM - h, MARGEM)
  const esquerdaBalao = Math.min(Math.max(left, MARGEM), vw - largura - MARGEM)

  const painel = 'pointer-events-auto absolute bg-[rgba(5,8,12,0.6)]'

  return createPortal(
    <div className="pointer-events-none fixed inset-0 z-[60]">
      <div className={painel} style={{ top: 0, left: 0, right: 0, height: furo.top }} />
      <div className={painel} style={{ top: furo.top + furo.height, left: 0, right: 0, bottom: 0 }} />
      <div className={painel} style={{ top: furo.top, left: 0, width: furo.left, height: furo.height }} />
      <div className={painel} style={{ top: furo.top, left: furo.left + furo.width, right: 0, height: furo.height }} />
      <div
        className="absolute rounded-lg"
        style={{ ...furo, boxShadow: '0 0 0 3px #FFB300, 0 0 18px 2px rgba(255,179,0,0.45)' }}
        aria-hidden
      />
      <div
        ref={balaoRef}
        role="dialog"
        aria-label={`Guia: ${passo.titulo}`}
        tabIndex={-1}
        className="pointer-events-auto absolute rounded-lg border border-primary bg-surface p-4 shadow-dropdown outline-none"
        style={{ top: topoBalao, left: esquerdaBalao, width: largura }}
      >
        <div aria-live="polite" className="space-y-1.5">
          <p className="text-2xs font-semibold uppercase tracking-wide text-primary">Passo {n} de {total}</p>
          <h5 className="text-15 font-semibold text-white">{passo.titulo}</h5>
          <p className="text-13 text-muted">{passo.texto}</p>
        </div>
        <div className="mt-3 flex items-center justify-between gap-2">
          <Button size="sm" variant="ghost" onClick={onPular}>Pular guia</Button>
          <div className="flex gap-2">
            <Button size="sm" variant="light" onClick={onVoltar} disabled={n === 1}>Voltar</Button>
            <Button size="sm" onClick={onProximo}>{n === total ? 'Concluir' : 'Próximo'}</Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
